import React, { useEffect, useState } from 'react';
import {
  reduxForm,
  Field,
  Form,
  InjectedFormProps,
  getFormValues
} from 'redux-form';
import { compose } from 'ramda';
import {
  Theme,
  createStyles,
  makeStyles,
  Grid,
  CircularProgress,
  Typography,
  Chip
} from '@material-ui/core';
import { Paper, Modal } from 'components/Base';
import Fields from 'components/Fields';
import Router from 'components/Router/Router';
import StoreProvider from 'store/store';
import { MetaType } from 'store/modules/Meta/types';
import { BoxWrapper, Button } from 'components/Base';
import { CustomerType } from 'store/modules/Customers/types';
import QuestionnaireRequest from './QuestionnaireRequest/QuestionnaireRequest';
import classNames from 'classnames';
import { apiInstance } from 'api/utils';

export interface RiskProfileTypes {
  knowledgeAndExperience: MetaType;
  riskLevels: MetaType;
  caseCustomers: Array<CustomerType>;
  timeHorizons: Array<MetaType>;
  hasCapacityForLossEnabled: boolean;
  hasRiskRequirementCapacityEnabled: boolean;
  values: any;
  createRiskProfile: Function;
}

const { connect, selectors } = StoreProvider;
const { useParams, useHistory } = Router;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: theme.spacing(3, 3)
    },
    wrappedField: {
      marginTop: '21px',
      marginBottom: '21px',
      marginLeft: '8px',
      paddingRight: '8px'
    },
    leftColumn: {
      '@media(min-width: 600)': {
        maxWidth: '362px'
      }
    },
    riskProfile: {
      label: { color: '#fff' },
      input: { color: '#fff' }
    },
    btn: {
      marginTop: theme.spacing(3)
    },
    header: {
      background: '#fff'
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1
    },
    field: {
      flex: 1
    },
    questionnareBox: {
      height: '4.125rem',
      borderBottom: '1px solid #eaeaea',
      display: 'flex',
      alignItems: 'center',
      padding: '1rem'
    },
    questionnareEmailModal: {
      padding: '7px 14px',
      transition: 'all 0.3s ease-in-out',
      overflow: 'hidden',
      height: '300px',
      width: '300px',
      position: 'relative'
    },
    questionnaireEmailBtn: {
      padding: '7px 14px',
      position: 'absolute',
      bottom: '12px',
      right: '12px'
    },
    riskWrapper: {
      padding: '1.5rem 1rem',
      borderRadius: '4px'
    },
    sendEmail: {
      marginBottom: '8px',
      padding: '2rem 1rem'
    },
    roundedBox: {
      boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.25)',
      borderRadius: ' 8px'
    },
    labelNoWrap: {
      '& .MuiInputLabel-root': {
        whiteSpace: 'nowrap'
      }
    }
  })
);

const getSuggestedCap = (
  timeHorizon: string,
  composure: string,
  knowledge: string,
  experience: string
) => {
  const caps = {
    '1': 14,
    '102': 37,
    '304': 52,
    '506': 68
  };

  const composureCap = composure === '1' ? 68 : null;
  const knowledgeCap = knowledge === '1' ? 61 : knowledge === '2' ? 68 : null;
  const experienceCap = experience === '1' ? 68 : null;

  const timeHorizonCap = caps[timeHorizon as keyof typeof caps] || null;

  const allCaps = [
    timeHorizonCap,
    composureCap,
    knowledgeCap,
    experienceCap
  ].filter((val): val is number => val !== null);

  return allCaps.length ? Math.min(...allCaps) : null;
};

const RiskProfile = ({
  handleSubmit,
  caseCustomers,
  createRiskProfile,
  values
}: RiskProfileTypes & InjectedFormProps) => {
  const [sendEmail, setSendEmail] = useState(false);
  const classes = useStyles();
  const history = useHistory();
  const { portfolio } = useParams() as any;
  const isMoreThanOneCustomer = caseCustomers.length > 1;
  const customers =
    isMoreThanOneCustomer &&
    (caseCustomers.filter(
      (customer: CustomerType) => !customer.is_jointed
    ) as CustomerType[]);
  const firstCustomer = customers && customers[0];
  const secondCustomer = customers && customers[1];
  const [composure, setComposure] = useState([]);
  const [experience, setExperience] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [timeHorizons, setTimeHorizons] = useState([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedCap, setSuggestedCap] = useState<number | null>(null);

  useEffect(() => {
    async function getData() {
      setIsLoading(true);
      try {
        const [
          composureResponse,
          experienceResponse,
          knowledgeResponse,
          timeHorizonsResponse
        ] = await Promise.all([
          apiInstance.request({
            method: 'GET',
            url: '/meta/composure/'
          }),
          apiInstance.request({
            method: 'GET',
            url: '/meta/experience/'
          }),
          apiInstance.request({
            method: 'GET',
            url: '/meta/knowledge/'
          }),
          apiInstance.request({
            method: 'GET',
            url: '/meta/morningstar-time-horizons/'
          })
        ]);
        setComposure(composureResponse.data);
        setExperience(experienceResponse.data);
        setKnowledge(knowledgeResponse.data);
        setTimeHorizons(timeHorizonsResponse.data);
      } catch (err) {
        setError((err as Error).message || 'Failed to fetch data');
      } finally {
        setIsLoading(false);
      }
    }

    getData();
  }, []);

  useEffect(() => {
    const { time_horizon_interval, composure, knowledge, experience } = values;

    const cap = getSuggestedCap(
      time_horizon_interval,
      composure,
      knowledge,
      experience
    );
    setSuggestedCap(cap);
  }, [values]);

  if (isLoading) {
    return (
      <div className={classes.root}>
        <CircularProgress size="20px" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={classes.root}>
        <Typography color="error" variant="body1">
          Error fetching data: {error}
        </Typography>
      </div>
    );
  }

  return (
    <Grid
      xs={12}
      sm={12}
      md={3}
      lg={3}
      xl={3}
      item
      className={classes.leftColumn}
    >
      <Form onSubmit={handleSubmit} className={classes.form}>
        <BoxWrapper>
          <div className={classes.questionnareBox}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setSendEmail(!sendEmail)}
            >
              ATR Questionnaire
            </Button>
            <Button
              style={{ marginLeft: '24px' }}
              variant="contained"
              color="primary"
              onClick={async () => {
                const customer = isMoreThanOneCustomer
                  ? caseCustomers.filter(customer => customer.is_jointed)[0]
                  : caseCustomers[0];

                await createRiskProfile();
                history.push(
                  `/${portfolio}/${customer.id}/experience-questions`
                );
              }}
            >
              Experience Questionnaire
            </Button>
          </div>
          <div
            style={{
              paddingLeft: '1rem',
              paddingRight: '1rem',
              paddingTop: '1rem'
            }}
          >
            <Field
              name="customer_objectives"
              shrink
              className={classes.wrappedField}
              fullWidth
              component={Fields.Text}
              multiline
              minRows={1}
              label="Client objectives"
              tooltip="Optional"
            />

            {isMoreThanOneCustomer ? (
              <>
                <Field
                  name="risk_tolerance"
                  shrink
                  className={classes.wrappedField}
                  fullWidth
                  displayEmpty
                  component={Fields.Text}
                  minRows={1}
                  label={`Risk tolerance - ${
                    firstCustomer && firstCustomer.display_name
                  }`}
                  tooltip="Based on completed risk tolerance questionnaire"
                />
                <Field
                  name="risk_tolerance_customer_2"
                  shrink
                  className={classes.wrappedField}
                  fullWidth
                  validate={Fields.Validators.required}
                  displayEmpty
                  component={Fields.Text}
                  minRows={1}
                  label={`Risk tolerance - ${
                    secondCustomer && secondCustomer.display_name
                  }`}
                  tooltip="Based on completed risk tolerance questionnaire"
                />
              </>
            ) : (
              <Field
                name="risk_tolerance"
                shrink
                className={classes.wrappedField}
                fullWidth
                displayEmpty
                component={Fields.Text}
                minRows={1}
                label="Risk tolerance"
                tooltip="Based on completed risk tolerance questionnaire"
              />
            )}

            <Field
              name="time_horizon_interval"
              shrink
              className={classes.wrappedField}
              fullWidth
              options={timeHorizons}
              component={Fields.Select}
              label="Investment time horizon (years)"
            />

            <Field
              name="composure"
              shrink
              className={classes.wrappedField}
              fullWidth
              displayEmpty
              options={composure}
              component={Fields.Select}
              label="Composure"
            />

            <Field
              name="experience"
              shrink
              className={classes.wrappedField}
              fullWidth
              displayEmpty
              options={experience}
              component={Fields.Select}
              label="Experience"
            />

            <Field
              name="knowledge"
              shrink
              className={classes.wrappedField}
              fullWidth
              displayEmpty
              options={knowledge}
              component={Fields.Select}
              label="Knowledge"
            />
          </div>
        </BoxWrapper>

        <Paper
          warn
          className={classNames(classes.riskWrapper, classes.roundedBox)}
        >
          {suggestedCap !== null ? (
            <Chip
              label={`Suggested Cap: ${suggestedCap}`}
              style={{
                color: 'white',
                backgroundColor: '#ba4977',
                marginBottom: '1rem'
              }}
            />
          ) : (
            <></>
          )}
          <Field
            name="recommended_risk_profile"
            shrink
            fullWidth
            className={classes.riskProfile}
            red
            displayEmpty
            component={Fields.Text}
            inputProps={{ style: { color: 'white' } }}
            InputLabelProps={{ shrink: true, style: { color: 'white' } }}
            minRows={1}
            tooltipTop={-16}
            label="Suitability Score"
          />
        </Paper>
      </Form>
      <Modal
        closeModalFunc={() => setSendEmail(false)}
        isOpen={sendEmail}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
      >
        <QuestionnaireRequest
          customers={caseCustomers as CustomerType[]}
          onClose={() => setSendEmail(false)}
          createRiskProfile={createRiskProfile}
        />
      </Modal>
    </Grid>
  );
};

export default compose(
  reduxForm({
    form: 'riskProfile',
    initialValues: {
      risk_capacity: null,
      time_horizon: null,
      suitability_score: null,
      composure: null,
      knowledge: null,
      experience: null
    },
    enableReinitialize: false
  }),
  connect((state: any) => ({
    knowledgeAndExperience:
      selectors.metaSelectors.knowledgeAndExperience(state),
    organizationData: selectors.authSelectors.organizationData(state),
    caseCustomers: selectors.caseSelectors.caseCustomers(state),
    riskLevels: selectors.metaSelectors.riskLevels(state),
    values: getFormValues('riskProfile')(state),
    cipThresHolds: selectors.metaSelectors.cipThresHolds(state),
    timeHorizons: selectors.metaSelectors.timeHorizons(state),
    hasCapacityForLossEnabled:
      selectors.capacityForLossSelectors.hasCapacityForLossEnabled(state),
    hasRiskRequirementCapacityEnabled:
      selectors.capacityForLossSelectors.hasRiskRequirementCapacityEnabled(
        state
      )
  }))
)(RiskProfile);
